// File: src/redux/api/tickets.api.ts

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  query,
  where,
  getDoc,
  runTransaction,
  writeBatch,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import { 
  PriceDeformation, 
  AccessRestriction,
  isPriceDeformation,
} from 'src/@types/ticket';
import { Ticket } from 'src/@types/events_org';

// Constants
const EMAIL_THRESHOLD = 100; // Number of emails when we should use subcollection

// Function to create a document ID from an email
const createEmailDocId = (email: string): string => {
  // Convert email to lowercase and trim
  const normalizedEmail = email.toLowerCase().trim();
  // Create a base64 representation of the email (safe for Firestore document IDs)
  return btoa(normalizedEmail);
};

export const TicketsApi = createApi({
  reducerPath: 'ticketsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['PriceDeformations', 'AccessRestrictions', 'Tickets', 'EmailSubcollection'],
  endpoints: (builder) => ({
    // Price Deformations
    fetchPriceDeformations: builder.query<PriceDeformation[], string>({
      async queryFn(eventId) {
        try {
          const deformationsRef = collection(DB, 'events', eventId, 'priceDeformations');
          const querySnapshot = await getDocs(deformationsRef);
          const deformations: PriceDeformation[] = [];
          
          querySnapshot.forEach((doc) => {
            const data = { id: doc.id, ...doc.data() } as PriceDeformation;
            deformations.push(data);
          });
          
          return { 
            data: deformations.sort((a, b) => a.priority - b.priority) 
          };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: ['PriceDeformations'],
    }),

    addPriceDeformation: builder.mutation<void, { 
      eventId: string, 
      deformation: Omit<PriceDeformation, 'id'> 
    }>({
      async queryFn({ eventId, deformation }) {
        try {
          const deformationsRef = collection(DB, 'events', eventId, 'priceDeformations');
          await addDoc(deformationsRef, deformation);
          return { data: undefined };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ['PriceDeformations'],
    }),

    updatePriceDeformation: builder.mutation<void, {
      eventId: string,
      deformation: PriceDeformation
    }>({
      async queryFn({ eventId, deformation }) {
        try {
          const { id, ...updateData } = deformation;
          const deformationRef = doc(DB, 'events', eventId, 'priceDeformations', id);
          await updateDoc(deformationRef, updateData);
          return { data: undefined };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ['PriceDeformations'],
    }),

    deletePriceDeformation: builder.mutation<void, {
      eventId: string,
      deformationId: string
    }>({
      async queryFn({ eventId, deformationId }) {
        try {
          const deformationRef = doc(DB, 'events', eventId, 'priceDeformations', deformationId);
          await deleteDoc(deformationRef);
          return { data: undefined };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ['PriceDeformations'],
    }),

    // Access Restrictions
    fetchAccessRestrictions: builder.query<AccessRestriction[], string>({
      async queryFn(eventId) {
        try {
          const restrictionsRef = collection(DB, 'events', eventId, 'accessRestrictions');
          const querySnapshot = await getDocs(restrictionsRef);
          const restrictions: AccessRestriction[] = [];
          
          querySnapshot.forEach((doc) => {
            const data = { id: doc.id, ...doc.data() } as AccessRestriction;
            
            // Convert to standard format for the frontend
            // This ensures backwards compatibility
            if (data.useEmailSubcollection) {
              // For display purposes, show a placeholder if emails are in subcollection
              if (!data.condition.emails) {
                data.condition.emails = [];
              }
              // Add a flag to indicate this is using subcollection storage
              (data as any).isUsingEmailSubcollection = true;
            }
            
            restrictions.push(data);
          });
          
          return { 
            data: restrictions.sort((a, b) => a.priority - b.priority) 
          };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: ['AccessRestrictions'],
    }),

    // Fetch a preview of emails for a restriction with email subcollection
    fetchEmailPreview: builder.query<{
      emails: string[], 
      totalCount: number
    }, {
      eventId: string,
      restrictionId: string,
      previewCount?: number
    }>({
      async queryFn({ eventId, restrictionId, previewCount = 50 }) {
        try {
          // Get the restriction to check if it uses subcollection for emails
          const restrictionRef = doc(DB, 'events', eventId, 'accessRestrictions', restrictionId);
          const restrictionDoc = await getDoc(restrictionRef);
          
          if (!restrictionDoc.exists()) {
            throw new Error(`Restriction with ID ${restrictionId} not found`);
          }
          
          const restriction = restrictionDoc.data();
          
          // If it doesn't use email subcollection, return the emails array directly
          if (!restriction.useEmailSubcollection) {
            const emails = restriction.condition?.emails || [];
            return { 
              data: {
                emails,
                totalCount: emails.length
              }
            };
          }
          
          // Otherwise, fetch a sample of emails from the subcollection
          const emailsRef = collection(DB, 'events', eventId, 'accessRestrictions', restrictionId, 'emails');
          // Limit the query to just get a preview
          const emailsQuery = query(emailsRef, limit(previewCount));
          const emailsSnapshot = await getDocs(emailsQuery);
          
          // Extract emails from the documents
          const previewEmails = emailsSnapshot.docs.map(doc => doc.data().email);
          // Get the total count from the restriction document
          const totalEmailCount = restriction.emailCount || 0;
          
          return { 
            data: {
              emails: previewEmails,
              totalCount: totalEmailCount
            }
          };
        } catch (error: any) {
          console.error("Failed to fetch email preview:", error);
          return { error: error.message };
        }
      },
      providesTags: ['EmailSubcollection'],
    }),

    addAccessRestriction: builder.mutation<string, { 
      eventId: string, 
      restriction: Omit<AccessRestriction, 'id'> 
    }>({
      async queryFn({ eventId, restriction }) {
        try {
          console.log("API received restriction:", JSON.stringify(restriction, null, 2));
          
          // Extract emails from the condition object
          let emails: string[] = [];
          if (restriction.condition.type === 'EMAIL_ADDRESS' && 
              restriction.condition.emails && 
              restriction.condition.emails.length > 0) {
            emails = restriction.condition.emails;
          }
          
          // Create the restriction document first
          const restrictionsRef = collection(DB, 'events', eventId, 'accessRestrictions');
          
          // Prepare a sample of emails for the main document
          // If the original condition already had a sample, use that
          // Otherwise take up to 20 emails for display purposes
          const existingSample = restriction.condition.emails || [];
          const emailSample = existingSample.length > 0 && existingSample.length <= 20 
            ? existingSample 
            : emails.slice(0, 20);
          
          // Create restriction document
          const docRef = await addDoc(restrictionsRef, {
            ...restriction,
            // Store only a sample of emails in the main document
            condition: {
              ...restriction.condition,
              emails: emailSample
            },
            useEmailSubcollection: true, // Always use subcollection
            emailCount: emails.length,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          
          // Always store ALL emails in subcollection for email verification
          await storeEmailsInSubcollection(emails, eventId, docRef.id);
          
          return { data: docRef.id };
        } catch (error: any) {
          console.error("Failed to add access restriction:", error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['AccessRestrictions', 'EmailSubcollection'],
    }),

    // Update access restriction with support for large email lists
    updateAccessRestriction: builder.mutation<void, {
      eventId: string,
      restriction: AccessRestriction,
      emails?: string[]  // Optional parameter to update emails separately
    }>({
      async queryFn({ eventId, restriction, emails }) {
        try {
          const { id, ...updateData } = restriction;
          const restrictionRef = doc(DB, 'events', eventId, 'accessRestrictions', id);
          
          // Get current restriction data
          const currentDoc = await getDoc(restrictionRef);
          if (!currentDoc.exists()) {
            throw new Error(`Restriction with ID ${id} not found`);
          }
          
          const currentRestriction = currentDoc.data();
          const wasUsingSubcollection = currentRestriction.useEmailSubcollection === true;
          
          // If this is an EMAIL_ADDRESS restriction, handle email storage appropriately
          if (restriction.condition.type === 'EMAIL_ADDRESS') {
            // Determine which emails to use (explicit parameter or from condition)
            const newEmails = emails || restriction.condition.emails || [];
            console.log("New emails to update:", newEmails);
            console.log("Current emails in restriction:", currentRestriction.condition?.emails);
            
            // Always use subcollection approach for email-based restrictions
            if (!wasUsingSubcollection) {
              // Get current emails if they exist
              const currentEmails = currentRestriction.condition?.emails || [];
              
              // Prepare a sample of emails for the main document
              const existingSample = currentEmails.length > 0 && currentEmails.length <= 20 
                ? currentEmails 
                : newEmails.slice(0, 20);
              
              // Update restriction document to use subcollection
              await updateDoc(restrictionRef, {
                ...updateData,
                condition: {
                  ...updateData.condition,
                  emails: existingSample
                },
                useEmailSubcollection: true,
                emailCount: newEmails.length,
                updatedAt: serverTimestamp()
              });
              
              // Store emails in subcollection (all emails, including existing ones)
              await storeEmailsInSubcollection(newEmails, eventId, id);
            } else {
              // Already using subcollection - append new emails
              // Prepare a sample of emails for the main document
              let emailSample = currentRestriction.condition?.emails || [];
              if (emailSample.length < 20 && newEmails.length > 0) {
                // Add some of the new emails to sample if there's room
                const neededForSample = Math.min(20 - emailSample.length, newEmails.length);
                emailSample = [...emailSample, ...newEmails.slice(0, neededForSample)];
              }
              
              // Update restriction document
              await updateDoc(restrictionRef, {
                ...updateData,
                condition: {
                  ...updateData.condition,
                  emails: emailSample
                },
                // Update total email count by adding new emails
                emailCount: (currentRestriction.emailCount || 0) + newEmails.length,
                updatedAt: serverTimestamp()
              });
              
              // Append new emails to the subcollection without deleting existing ones
              if (newEmails.length > 0) {
                await appendEmailsToSubcollection(newEmails, eventId, id);
              }
            }
          } else {
            // For non-EMAIL_ADDRESS restrictions, use standard update
            await updateDoc(restrictionRef, {
              ...updateData,
              updatedAt: serverTimestamp()
            });
          }
          
          // Update affected tickets
          await updateAffectedTickets(eventId, id, restriction);
          
          return { data: undefined };
        } catch (error: any) {
          console.error("Failed to update access restriction:", error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['AccessRestrictions', 'EmailSubcollection', 'Tickets'],
    }),

    // Updated delete method with efficient email subcollection handling
    deleteAccessRestriction: builder.mutation<void, {
      eventId: string,
      restrictionId: string
    }>({
      async queryFn({ eventId, restrictionId }) {
        try {
          // Check if this restriction is used by any tickets
          const ticketsRef = collection(DB, 'events', eventId, 'tickets');
          const ticketsQuery = query(ticketsRef, where("accessRestrictions", "array-contains", restrictionId));
          const ticketsSnapshot = await getDocs(ticketsQuery);
          
          // Get the restriction to check if it uses email subcollection
          const restrictionRef = doc(DB, 'events', eventId, 'accessRestrictions', restrictionId);
          const restrictionDoc = await getDoc(restrictionRef);
          
          if (!restrictionDoc.exists()) {
            throw new Error(`Restriction with ID ${restrictionId} not found`);
          }
          
          const restriction = restrictionDoc.data();
          const usesEmailSubcollection = restriction.useEmailSubcollection === true;
          
          if (ticketsSnapshot.size > 0) {
            // Remove the restriction from all tickets
            const batch = writeBatch(DB);
            
            ticketsSnapshot.forEach((ticketDoc) => {
              const ticketData = ticketDoc.data() as Ticket;
              // Remove this restriction ID from the ticket's accessRestrictions array
              const updatedRestrictions = (ticketData.accessRestrictions || [])
                .filter((accessRestriction: any) => {
                  if (typeof accessRestriction === 'string') {
                    return accessRestriction !== restrictionId;
                  } else if (accessRestriction && typeof accessRestriction === 'object') {
                    return accessRestriction.id !== restrictionId;
                  }
                  return true;
                });
              
              batch.update(ticketDoc.ref, { accessRestrictions: updatedRestrictions });
            });
            
            // Delete the restriction
            batch.delete(restrictionRef);
            
            // Commit the batch
            await batch.commit();
          } else {
            // No tickets reference this restriction, simply delete it
            await deleteDoc(restrictionRef);
          }
          
          // If the restriction uses an email subcollection, delete it
          if (usesEmailSubcollection) {
            await deleteEmailsSubcollection(eventId, restrictionId);
          }
          
          return { data: undefined };
        } catch (error: any) {
          console.error("Failed to delete access restriction:", error);
          return { error: error.message };
        }
      },
      invalidatesTags: ['AccessRestrictions', 'EmailSubcollection', 'Tickets'],
    }),
    
    duplicateTicket: builder.mutation<string, {
      eventId: string,
      ticketId: string,
      newTicketData?: Partial<Ticket> // Optional overrides for the duplicated ticket
    }>({
      async queryFn({ eventId, ticketId, newTicketData }) {
        try {
          // Get the original ticket
          const ticketRef = doc(DB, "events", eventId, "tickets", ticketId);
          const ticketDoc = await getDoc(ticketRef);
          
          if (!ticketDoc.exists()) {
            throw new Error(`Ticket with ID ${ticketId} not found`);
          }
          
          // Get the original ticket data
          const originalTicket = ticketDoc.data() as Ticket;
          
          // Create a new ticket with the same properties but with a new ID
          // and update any properties that should be different in the duplicate
          const duplicatedTicket = {
            ...originalTicket,
            // Override these default properties
            id: undefined, // Will be auto-generated by Firestore
            name: `${originalTicket.name} (Copy)`,
            order: originalTicket.order + 1, // Place it right after the original ticket
            createdAt: new Date(),
            updatedAt: new Date(),
            // Apply any custom overrides provided
            ...newTicketData
          };
          
          // Add the new ticket to the collection
          const newTicketRef = await addDoc(
            collection(DB, "events", eventId, "tickets"),
            duplicatedTicket
          );
          
          // Update the order of all other tickets as needed
          const ticketsRef = collection(DB, "events", eventId, "tickets");
          const ticketsSnapshot = await getDocs(ticketsRef);
          
          const batch = writeBatch(DB);
          
          // Reorder tickets that come after the duplicated ticket
          ticketsSnapshot.forEach(doc => {
            const ticketData = doc.data() as Ticket;
            if (
              ticketData.order > originalTicket.order && 
              doc.id !== newTicketRef.id // don't update the newly created ticket
            ) {
              batch.update(doc.ref, { order: ticketData.order + 1 });
            }
          });
          
          await batch.commit();
          
          return { data: newTicketRef.id };
        } catch (error: any) {
          console.error("Failed to duplicate ticket:", error);
          return { error: error.message };
        }
      },
      invalidatesTags: ["Tickets"],
    }),
    
    // Utility function to check if a restriction is in use
    checkRestrictionInUse: builder.mutation<{ inUse: boolean, ticketCount: number }, { 
      eventId: string, 
      restrictionId: string 
    }>({
      async queryFn({ eventId, restrictionId }) {
        try {
          // Simply get all tickets for the event
          const ticketsRef = collection(DB, 'events', eventId, 'tickets');
          const ticketsSnapshot = await getDocs(ticketsRef);
          
          // Filter tickets that have the restriction ID
          const ticketsWithRestriction = ticketsSnapshot.docs.filter(doc => {
            const ticketData = doc.data();
            
            // Check if accessRestrictions is present and non-empty
            if (Array.isArray(ticketData.accessRestrictions) && ticketData.accessRestrictions.length > 0) {
              return ticketData.accessRestrictions.some((restriction: any) => {
                // If it's a string ID (normalized format)
                if (typeof restriction === 'string') {
                  return restriction === restrictionId;
                }
                
                // If it's an object with an ID field
                if (typeof restriction === 'object' && restriction !== null) {
                  return restriction.id === restrictionId;
                }
                
                return false;
              });
            }
            
            // Also check the old accessRestrictionId field for backward compatibility
            if (ticketData.accessRestrictionId === restrictionId) {
              return true;
            }
            
            return false;
          });
          
          return { 
            data: { 
              inUse: ticketsWithRestriction.length > 0,
              ticketCount: ticketsWithRestriction.length
            } 
          };
        } catch (error: any) {
          console.error("Error checking restriction usage:", error);
          return { error: error.message };
        }
      },
    }),

    // Check if an email is allowed by a specific restriction
    checkEmailAllowed: builder.mutation<{ 
      allowed: boolean,
      reason: string
    }, {
      eventId: string,
      restrictionId: string,
      email: string
    }>({
      async queryFn({ eventId, restrictionId, email }) {
        try {
          // Get the restriction
          const restrictionRef = doc(DB, 'events', eventId, 'accessRestrictions', restrictionId);
          const restrictionDoc = await getDoc(restrictionRef);
          
          if (!restrictionDoc.exists()) {
            return { 
              data: { 
                allowed: false, 
                reason: 'restriction_not_found' 
              } 
            };
          }
          
          const restriction = restrictionDoc.data() as AccessRestriction;
          
          // If restriction is not active, it doesn't apply
          if (!restriction.isActive) {
            return {
              data: {
                allowed: true,
                reason: 'restriction_not_active'
              }
            };
          }
          
          // For EMAIL_DOMAIN restrictions, check domain
          if (restriction.condition.type === 'EMAIL_DOMAIN') {
            const normalizedEmail = email.toLowerCase().trim();
            const domain = normalizedEmail.split('@')[1];
            
            if (!domain) {
              return {
                data: {
                  allowed: false,
                  reason: 'invalid_email_format'
                }
              };
            }
            
            const allowed = restriction.condition.domains?.some(
              restrictionDomain => restrictionDomain.toLowerCase().trim() === domain
            ) || false;
            
            return {
              data: {
                allowed,
                reason: allowed ? 'domain_allowed' : 'domain_not_allowed'
              }
            };
          }
          
          // For EMAIL_ADDRESS restrictions
          if (restriction.condition.type === 'EMAIL_ADDRESS') {
            const normalizedEmail = email.toLowerCase().trim();
            
            // If using subcollection, check if email exists in the subcollection
            if (restriction.useEmailSubcollection) {
              const emailDocId = createEmailDocId(normalizedEmail);
              const emailDocRef = doc(
                DB, 
                'events', 
                eventId, 
                'accessRestrictions', 
                restrictionId, 
                'emails', 
                emailDocId
              );
              
              const emailDoc = await getDoc(emailDocRef);
              const allowed = emailDoc.exists();
              
              return {
                data: {
                  allowed,
                  reason: allowed ? 'email_allowed' : 'email_not_allowed'
                }
              };
            } else {
              // Using email array in the document
              const allowed = restriction.condition.emails?.some(
                restrictionEmail => restrictionEmail.toLowerCase().trim() === normalizedEmail
              ) || false;
              
              return {
                data: {
                  allowed,
                  reason: allowed ? 'email_allowed' : 'email_not_allowed'
                }
              };
            }
          }
          
          // Fallback for unsupported condition types
          return {
            data: {
              allowed: false,
              reason: 'unsupported_condition_type'
            }
          };
        } catch (error: any) {
          console.error("Error checking if email is allowed:", error);
          return { error: error.message };
        }
      }
    })
  }),
});

// Helper function to store emails in a subcollection with email-based IDs
async function storeEmailsInSubcollection(emails: string[], eventId: string, restrictionId: string) {
  try {
    // Create the emails collection reference
    const emailsRef = collection(DB, 'events', eventId, 'accessRestrictions', restrictionId, 'emails');
    
    // Process emails in batches to avoid exceeding Firestore write limits
    const MAX_BATCH_SIZE = 500; // Firestore batch limit
    
    for (let i = 0; i < emails.length; i += MAX_BATCH_SIZE) {
      const batch = writeBatch(DB);
      const emailBatch = emails.slice(i, i + MAX_BATCH_SIZE);
      
      // Add each email as a document with ID derived from the email itself
      for (const email of emailBatch) {
        const normalizedEmail = email.toLowerCase().trim();
        const emailDocId = createEmailDocId(normalizedEmail);
        
        batch.set(doc(emailsRef, emailDocId), {
          email: normalizedEmail,
          createdAt: serverTimestamp()
        });
      }
      
      // Commit the batch
      await batch.commit();
    }
  } catch (error) {
    console.error("Failed to store emails in subcollection:", error);
    throw error;
  }
}

async function appendEmailsToSubcollection(newEmails: string[], eventId: string, restrictionId: string) {
  try {
    // Create the emails collection reference
    const emailsRef = collection(DB, 'events', eventId, 'accessRestrictions', restrictionId, 'emails');
    
    // Process emails in batches to avoid exceeding Firestore write limits
    const MAX_BATCH_SIZE = 500; // Firestore batch limit
    
    for (let i = 0; i < newEmails.length; i += MAX_BATCH_SIZE) {
      const batch = writeBatch(DB);
      const emailBatch = newEmails.slice(i, i + MAX_BATCH_SIZE);
      
      // Add each email as a document with ID derived from the email itself
      for (const email of emailBatch) {
        const normalizedEmail = email.toLowerCase().trim();
        const emailDocId = createEmailDocId(normalizedEmail);
        
        // Use set with merge option to avoid overwriting if the email already exists
        batch.set(doc(emailsRef, emailDocId), {
          email: normalizedEmail,
          createdAt: serverTimestamp()
        }, { merge: true });
      }
      
      // Commit the batch
      await batch.commit();
    }
  } catch (error) {
    console.error("Failed to append emails to subcollection:", error);
    throw error;
  }
}

// Helper function to delete all emails in the subcollection
async function deleteEmailsSubcollection(eventId: string, restrictionId: string) {
  try {
    const emailsRef = collection(DB, 'events', eventId, 'accessRestrictions', restrictionId, 'emails');
    const emailsSnapshot = await getDocs(emailsRef);
    
    // Delete emails in batches if there are many
    const MAX_BATCH_SIZE = 500; // Firestore batch limit
    let batch = writeBatch(DB);
    let batchCount = 0;
    
    for (const emailDoc of emailsSnapshot.docs) {
      batch.delete(emailDoc.ref);
      batchCount++;
      
      // If we've reached the batch limit, commit and create a new batch
      if (batchCount >= MAX_BATCH_SIZE) {
        await batch.commit();
        batch = writeBatch(DB);
        batchCount = 0;
      }
    }
    
    // Commit any remaining deletes
    if (batchCount > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.error("Failed to delete emails subcollection:", error);
    throw error;
  }
}

// Helper function to update tickets that reference a restriction
async function updateAffectedTickets(eventId: string, restrictionId: string, updatedRestriction: AccessRestriction) {
  try {
    // Get all tickets that reference this restriction
    const ticketsRef = collection(DB, 'events', eventId, 'tickets');
    const ticketsSnapshot = await getDocs(ticketsRef);
    
    // Filter tickets that have the restriction ID
    const affectedTickets = ticketsSnapshot.docs.filter(doc => {
      const ticketData = doc.data();
      
      // Check if accessRestrictions array contains the restriction ID
      if (Array.isArray(ticketData.accessRestrictions)) {
        return ticketData.accessRestrictions.some((restriction: any) => {
          // If it's a string ID
          if (typeof restriction === 'string') {
            return restriction === restrictionId;
          }
          
          // If it's an object with an ID field
          if (typeof restriction === 'object' && restriction !== null) {
            return restriction.id === restrictionId;
          }
          
          return false;
        });
      }
      
      // Also check the old accessRestrictionId field
      return ticketData.accessRestrictionId === restrictionId;
    });
    
    if (affectedTickets.length === 0) {
      return; // No tickets to update
    }
    
    // Use a batch to update all affected tickets
    const MAX_BATCH_SIZE = 500; // Firestore batch limit
    let batch = writeBatch(DB);
    let batchCount = 0;
    
    for (const ticketDoc of affectedTickets) {
      const ticketData = ticketDoc.data();
      let updatedTicket = false;
      let updatedRestrictions = [];
      
      // Handle accessRestrictions array
      if (Array.isArray(ticketData.accessRestrictions)) {
        updatedRestrictions = ticketData.accessRestrictions.map((item: any) => {
          // If it's a string ID, keep it as is
          if (typeof item === 'string') {
            return item;
          }
          
          // If it's an object that matches our target restriction ID, replace it with updated version
          if (typeof item === 'object' && item !== null && item.id === restrictionId) {
            updatedTicket = true;
            // Create a simple version of the restriction suitable for embedding
            return {
              id: restrictionId,
              name: updatedRestriction.name,
              description: updatedRestriction.description,
              type: 'ACCESS_RESTRICTION',
              maxTicketsPerEmail: updatedRestriction.maxTicketsPerEmail
            };
          }
          
          // For any other objects, keep them unchanged
          return item;
        });
      }
      
      // Only update if we actually changed something
      if (updatedTicket) {
        batch.update(ticketDoc.ref, { accessRestrictions: updatedRestrictions });
        batchCount++;
        
        // If we've reached the batch limit, commit and create a new batch
        if (batchCount >= MAX_BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(DB);
          batchCount = 0;
        }
      }
    }
    
    // Commit any remaining updates
    if (batchCount > 0) {
      await batch.commit();
    }
  } catch (error) {
    console.error("Failed to update affected tickets:", error);
    throw error;
  }
}

export const {
  // Price Deformations exports
  useFetchPriceDeformationsQuery,
  useAddPriceDeformationMutation,
  useUpdatePriceDeformationMutation,
  useDeletePriceDeformationMutation,
  useDuplicateTicketMutation,
  
  // Access Restrictions exports
  useFetchAccessRestrictionsQuery,
  useAddAccessRestrictionMutation,
  useUpdateAccessRestrictionMutation,
  useDeleteAccessRestrictionMutation,
  useCheckRestrictionInUseMutation,
  
  // Email subcollection
  useFetchEmailPreviewQuery,
  useCheckEmailAllowedMutation,
} = TicketsApi;